import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Plus, Send } from "lucide-react";
import { useAppContext } from "../context/AppContext";
import { apiFetch, queryString } from "../lib/api";
import type { Paginated, ScenarioRecord, SectionListItem } from "../shared/schemas";
import { EmptyState, ErrorState, LoadingState, PageHeader, StatusBadge } from "../components/ui";

interface ScenarioDetail {
  scenario: Record<string, string | number | null>;
  overrides: Array<Record<string, string | number | null>>;
}

interface ScenarioAnalysis {
  conflictCount: number;
  bottleneckCount: number;
  underutilizedCount: number;
  overCapacityCount: number;
  averageUtilization: number;
}

export function ScenariosPage() {
  const { snapshotTermId, departmentId, user } = useAppContext();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState("");
  const [name, setName] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [maxEnrollment, setMaxEnrollment] = useState("");
  const [room, setRoom] = useState("");
  const [dayCode, setDayCode] = useState("");
  const [startMinutes, setStartMinutes] = useState("");
  const [endMinutes, setEndMinutes] = useState("");
  const scenariosQuery = useQuery({ queryKey: ["scenarios"], queryFn: () => apiFetch<ScenarioRecord[]>("/scenarios") });
  const detailQuery = useQuery({
    queryKey: ["scenario", selectedId],
    queryFn: () => apiFetch<ScenarioDetail>(`/scenarios/${selectedId}`),
    enabled: Boolean(selectedId),
  });
  const analysisQuery = useQuery({
    queryKey: ["scenario-analysis", selectedId],
    queryFn: () => apiFetch<ScenarioAnalysis>(`/scenarios/${selectedId}/analysis`),
    enabled: Boolean(selectedId),
  });
  const selectedScenario = scenariosQuery.data?.find((scenario) => scenario.id === selectedId);
  const sectionsQuery = useQuery({
    queryKey: ["scenario-sections", selectedScenario?.snapshotTermId, departmentId],
    queryFn: () => apiFetch<Paginated<SectionListItem>>(`/sections${queryString({ snapshotTermId: selectedScenario?.snapshotTermId, department: departmentId, pageSize: 100 })}`),
    enabled: Boolean(selectedScenario),
  });
  useEffect(() => {
    if (!selectedId && scenariosQuery.data?.length) setSelectedId(scenariosQuery.data[0].id);
  }, [scenariosQuery.data, selectedId]);
  const createMutation = useMutation({
    mutationFn: () => apiFetch<{ id: string }>("/scenarios", { method: "POST", body: JSON.stringify({ snapshotTermId, name }) }),
    onSuccess: async (result) => { setName(""); setSelectedId(result.id); await queryClient.invalidateQueries({ queryKey: ["scenarios"] }); },
  });
  const overrideMutation = useMutation({
    mutationFn: () => apiFetch(`/scenarios/${selectedId}/overrides`, {
      method: "PUT",
      body: JSON.stringify({
        sectionId,
        maxEnrollment: maxEnrollment ? Number(maxEnrollment) : null,
        room: room || null,
        dayCode: dayCode || null,
        startMinutes: startMinutes ? Number(startMinutes) : null,
        endMinutes: endMinutes ? Number(endMinutes) : null,
      }),
    }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["scenario", selectedId] }),
        queryClient.invalidateQueries({ queryKey: ["scenario-analysis", selectedId] }),
        queryClient.invalidateQueries({ queryKey: ["scenarios"] }),
      ]);
    },
  });
  const publishMutation = useMutation({
    mutationFn: () => apiFetch(`/scenarios/${selectedId}/publish`, { method: "POST" }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ["scenarios"] }); await queryClient.invalidateQueries({ queryKey: ["scenario", selectedId] }); },
  });
  if (scenariosQuery.isLoading) return <LoadingState />;
  if (scenariosQuery.error) return <ErrorState error={scenariosQuery.error} />;
  const canCreate = user.role !== "faculty";
  const canPublish = user.role === "administrator" || user.role === "scheduler";

  return (
    <>
      <PageHeader title="Schedule Scenarios" description="Test proposed capacity and meeting changes without modifying immutable imported snapshots." />
      {canCreate && (
        <div className="panel flex flex-col gap-3 p-4 sm:flex-row">
          <input className="field flex-1" value={name} onChange={(event) => setName(event.target.value)} placeholder="New private scenario name" />
          <button className="button-primary" disabled={!name || !snapshotTermId || createMutation.isPending} onClick={() => createMutation.mutate()}><Plus className="h-4 w-4" /> Create from selected snapshot</button>
        </div>
      )}
      {scenariosQuery.data?.length === 0 ? (
        <EmptyState title="No scenarios" description="Create a private draft from the selected snapshot to test proposed changes." />
      ) : (
        <div className="grid gap-6 xl:grid-cols-[320px_1fr]">
          <aside className="panel divide-y divide-slate-100 overflow-hidden">
            {scenariosQuery.data?.map((scenario) => (
              <button key={scenario.id} className={`w-full p-4 text-left transition ${selectedId === scenario.id ? "bg-brand-50" : "hover:bg-slate-50"}`} onClick={() => setSelectedId(scenario.id)}>
                <div className="flex items-start justify-between gap-2"><p className="font-bold text-slate-900">{scenario.name}</p><StatusBadge status={scenario.status} /></div>
                <p className="mt-1 text-xs text-slate-500">Term {scenario.termCode} · {scenario.overrideCount} changes</p>
                <p className="mt-2 truncate text-xs text-slate-400">{scenario.ownerEmail}</p>
              </button>
            ))}
          </aside>
          <section className="space-y-4">
            {detailQuery.isLoading && <LoadingState />}
            {detailQuery.error && <ErrorState error={detailQuery.error} />}
            {selectedScenario && detailQuery.data && (
              <>
                <div className="panel flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between">
                  <div><h2 className="text-xl font-black text-slate-950">{selectedScenario.name}</h2><p className="mt-1 text-sm text-slate-500">Term {selectedScenario.termCode} · Owner {selectedScenario.ownerEmail}</p></div>
                  <div className="flex gap-2">
                    <a className="button-secondary" href={`/api/scenarios/${selectedId}/export`}><Download className="h-4 w-4" /> Export CSV</a>
                    {canPublish && selectedScenario.status === "draft" && <button className="button-primary" onClick={() => publishMutation.mutate()}><Send className="h-4 w-4" /> Publish</button>}
                  </div>
                </div>
                {analysisQuery.data && (
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                    {[
                      ["Conflicts", analysisQuery.data.conflictCount],
                      ["Bottlenecks", analysisQuery.data.bottleneckCount],
                      ["Underutilized", analysisQuery.data.underutilizedCount],
                      ["Over Capacity", analysisQuery.data.overCapacityCount],
                      ["Avg Utilization", `${(analysisQuery.data.averageUtilization * 100).toFixed(1)}%`],
                    ].map(([label, value]) => (
                      <div key={label} className="panel p-4">
                        <p className="text-[10px] font-bold uppercase text-slate-400">{label}</p>
                        <p className="mt-1 text-xl font-black text-slate-900">{value}</p>
                      </div>
                    ))}
                  </div>
                )}
                {selectedScenario.status === "draft" && selectedScenario.ownerId === user.id && (
                  <div className="panel p-5">
                    <h3 className="font-bold text-slate-900">Add or update a section override</h3>
                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      <select className="field xl:col-span-2" value={sectionId} onChange={(event) => setSectionId(event.target.value)}>
                        <option value="">Select section</option>
                        {sectionsQuery.data?.items.map((section) => <option key={section.id} value={section.id}>{section.subject} {section.courseNumber}-{section.sectionCode} · CRN {section.crn}</option>)}
                      </select>
                      <input className="field" type="number" min="0" value={maxEnrollment} onChange={(event) => setMaxEnrollment(event.target.value)} placeholder="Proposed max enrollment" />
                      <input className="field" value={room} onChange={(event) => setRoom(event.target.value)} placeholder="Proposed room" />
                      <select className="field" value={dayCode} onChange={(event) => setDayCode(event.target.value)}><option value="">Keep day</option>{["M","T","W","R","F","S","U"].map((day) => <option key={day} value={day}>{day}</option>)}</select>
                      <div className="grid grid-cols-2 gap-3"><input className="field" type="number" min="0" max="1439" value={startMinutes} onChange={(event) => setStartMinutes(event.target.value)} placeholder="Start mins" /><input className="field" type="number" min="1" max="1440" value={endMinutes} onChange={(event) => setEndMinutes(event.target.value)} placeholder="End mins" /></div>
                    </div>
                    <button className="button-primary mt-4" disabled={!sectionId || overrideMutation.isPending} onClick={() => overrideMutation.mutate()}>Save override</button>
                    {overrideMutation.error && <p className="mt-3 text-sm text-rose-600">{overrideMutation.error.message}</p>}
                  </div>
                )}
                <div className="panel overflow-hidden">
                  <div className="border-b border-slate-200 px-5 py-4"><h3 className="font-bold text-slate-900">Proposed Changes</h3></div>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[800px] text-left text-sm">
                      <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Section</th><th className="px-4 py-3">CRN</th><th className="px-4 py-3">Max</th><th className="px-4 py-3">Room</th><th className="px-4 py-3">Day</th><th className="px-4 py-3">Time</th></tr></thead>
                      <tbody className="divide-y divide-slate-100">
                        {detailQuery.data.overrides.map((override) => <tr key={String(override.id)}><td className="px-4 py-3 font-bold">{String(override.subject)} {String(override.course_number)}-{String(override.section_code)}</td><td className="px-4 py-3">{String(override.crn)}</td><td className="px-4 py-3">{String(override.max_enrollment ?? "—")}</td><td className="px-4 py-3">{String(override.room ?? "—")}</td><td className="px-4 py-3">{String(override.day_code ?? "—")}</td><td className="px-4 py-3">{override.start_minutes != null ? `${String(override.start_minutes)}–${String(override.end_minutes)}` : "—"}</td></tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </>
  );
}
